import { CustomFontFamily } from "../brand-kit";

const CSS_IDENTIFIER_UNSAFE = /[^a-zA-Z0-9\-_]/;

/**
 * Quotes a font family name for safe use in CSS values (inline styles, @font-face, FontFace API).
 * Names containing characters that aren't valid in CSS identifiers (e.g. apostrophes, spaces)
 * must be quoted to avoid silent rejection by the CSS parser.
 */
export function cssFontFamily(name: string): string {
  if (!CSS_IDENTIFIER_UNSAFE.test(name)) {
    return name;
  }
  const escaped = name.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
  // We use single quotes here just to be safe, but either quote style works. Previously
  // html-minifier's minifyCSS option couldn't parse double quotes in HTML attributes;
  // we no longer use minifyCSS (see email-ssr/render-pipeline.ts) so it could be either.
  return `'${escaped}'`;
}

/**
 * Checks if a font family is a custom font
 */
export function isCustomFont(
  fontFamily: string,
  customFontFamilies?: CustomFontFamily[] | null,
): boolean {
  if (!customFontFamilies) {
    return false;
  }
  return customFontFamilies.some((cf) => cf.fontFamily === fontFamily);
}

/**
 * Gets a custom font family object by name
 */
export function getCustomFontFamily(
  fontFamily: string,
  customFontFamilies?: CustomFontFamily[] | null,
): CustomFontFamily | undefined {
  if (!customFontFamilies) {
    return undefined;
  }
  return customFontFamilies.find((cf) => cf.fontFamily === fontFamily);
}

/**
 * Gets the appropriate font-family CSS value with fallbacks.
 * For custom fonts, this returns the custom font with its specific fallback.
 * For system fonts, this returns the font with standard web-safe fallbacks.
 */
export function getFontFamilyWithFallback(
  fontFamily: string,
  customFontFamilies?: CustomFontFamily[] | null,
): string {
  if (!fontFamily) {
    return "Arial, sans-serif";
  }

  if (!customFontFamilies) {
    // For system fonts, add standard fallbacks if not already present
    if (!fontFamily.includes(",")) {
      return `${fontFamily}, Arial, sans-serif`;
    }
    return fontFamily;
  }

  const customFont = customFontFamilies.find(
    (cf) => cf.fontFamily === fontFamily,
  );

  if (customFont) {
    return `${cssFontFamily(customFont.fontFamily)}, ${customFont.fallbackFont}`;
  }

  // Not a custom font, add standard fallbacks if not already present
  if (!fontFamily.includes(",")) {
    return `${fontFamily}, Arial, sans-serif`;
  }
  return fontFamily;
}
